// Code-review note: the useEffect below omits stable React setters + lambda
// params from its dep array (that's how they're supposed to work). Component
// size is deferred for MVP velocity. See /app/REVIEW_WAIVERS.md §1, §3
// before flagging exhaustive-deps or complexity here.
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, ExternalLink, Instagram, Facebook } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { business } from '../../mock';
import PhotoUpload from './PhotoUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact({ source = 'contact_form', label, heading, subheading } = {}) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    business_name: '',
    message: '',
    agree: false,
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef(null);

  // Pre-fill message from "Get a Quote" service buttons
  useEffect(() => {
    // On initial mount, only apply if user hasn't typed anything
    const stored = sessionStorage.getItem('quote_service');
    if (stored) {
      setForm((f) => (f.message ? f : { ...f, message: stored }));
      sessionStorage.removeItem('quote_service');
    }
    // On explicit click, always overwrite and focus the Name field
    const handler = (e) => {
      if (!e.detail) return;
      setForm((f) => ({ ...f, message: e.detail }));
      // Wait for smooth-scroll to settle before focusing to avoid jarring jump
      setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 700);
    };
    window.addEventListener('prefill-quote', handler);
    return () => window.removeEventListener('prefill-quote', handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.message) {
      toast({ title: 'Please fill in name, phone, address and message', variant: 'destructive' });
      return;
    }
    if (!form.agree) {
      toast({ title: 'Please agree to terms & conditions', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        address: form.address,
        business_name: form.business_name || null,
        message: form.message,
        source: source,
        agreed_terms: form.agree,
        photos: photos.map(({ storage_path, filename, content_type, size_bytes }) => ({ storage_path, filename, content_type, size_bytes })),
      });
      toast({ title: 'Quote request received!', description: "We'll be in touch shortly with your free no obligation quote." });
      setForm({ name: '', phone: '', email: '', address: '', business_name: '', message: '', agree: false });
      setPhotos([]);
    } catch {
      toast({
        title: 'Something went wrong',
        description: 'Please try again or call us directly.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 cream-bg">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-14">
          <div className="lg:col-span-5 reveal">
            <p className="section-label mb-4">{label || 'Google Maps & Contact'}</p>
            <h2 className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.05] mb-6">
              {heading || 'Talk to our trusted local crew.'}
            </h2>
            <p className="text-[#212B20] text-lg leading-relaxed">
              {subheading || 'Real people answering the phone. Free no obligation quotes, clear pricing, and reliable scheduling on every job.'}
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="gold-top-card p-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-[#1B6E37]" />
                <span className="text-xs font-bold tracking-widest text-[#0A2E18] uppercase">Address</span>
              </div>
              <div className="font-bold text-[#0A2E18] mb-2">{business.name}</div>
              <div className="text-[#212B20] text-sm leading-relaxed">{business.address}</div>
              <a
                href="https://www.google.com/maps?q=Ballarat,Victoria,Australia&z=10"
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#1B6E37] hover:underline"
              >
                Open in Maps <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="gold-top-card p-6">
              <div className="flex items-center gap-2 mb-3">
                <Phone className="w-4 h-4 text-[#1B6E37]" />
                <span className="text-xs font-bold tracking-widest text-[#0A2E18] uppercase">Contact</span>
              </div>
              <a href={`tel:${business.phone}`} className="block font-bold text-[#0A2E18] mb-2 hover:text-[#1B6E37]">
                {business.phone}
              </a>
              <a href={`mailto:${business.email}`} className="inline-flex items-center gap-2 text-[#212B20] text-sm hover:text-[#1B6E37]">
                <Mail className="w-4 h-4" /> {business.email}
              </a>
              <div className="mt-3 pt-3 border-t border-[#E2DAC5] flex items-center justify-between gap-3">
                <div className="text-xs text-[#4A5447]">Mon to Fri, 9:00am to 5:00pm</div>
                <div className="flex items-center gap-2">
                  <a
                    href={business.socials.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Follow us on Facebook"
                    className="w-8 h-8 rounded-full bg-[#F2ECE0] hover:bg-[#0A2E18] hover:text-[#D98A00] text-[#0A2E18] flex items-center justify-center transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                  <a
                    href={business.socials.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Follow us on Instagram"
                    className="w-8 h-8 rounded-full bg-[#F2ECE0] hover:bg-[#0A2E18] hover:text-[#D98A00] text-[#0A2E18] flex items-center justify-center transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="min-h-[440px] grid">
            <div className="relative rounded-lg overflow-hidden border border-[#E2DAC5] shadow-sm bg-white">
              <iframe
                title="VGLM Ballarat service area map"
                src="https://maps.google.com/maps?q=Ballarat,Victoria,Australia&z=10&output=embed"
                width="100%" height="100%" style={{ border: 0, minHeight: 420 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                data-testid="footer-map-ballarat"
              />
              <div className="absolute top-3 left-3 bg-white rounded-lg shadow-lg border border-[#E2DAC5] px-3 py-2 max-w-[220px] pointer-events-none">
                <div className="h-1 -mx-3 -mt-2 mb-1.5 bg-gradient-to-r from-[#1B6E37] via-[#D98A00] to-[#B86F00] rounded-t-lg" />
                <div className="flex items-center gap-1.5 mb-0.5">
                  <MapPin className="w-3.5 h-3.5 text-[#1B6E37]" />
                  <span className="text-[10px] font-bold tracking-widest text-[#0A2E18] uppercase">Service Area</span>
                </div>
                <div className="font-bold text-[#0A2E18] text-sm leading-tight">Ballarat</div>
                <div className="text-[#212B20] text-[11px] mt-0.5">Up to 30 km radius</div>
              </div>
            </div>
          </div>

          <div className="gold-top-card p-8">
            <p className="section-label mb-3">Free Quote</p>
            <h3 className="text-3xl font-extrabold mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Send us the property details.
            </h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">CONTACT NAME *</label>
                  <input type="text" ref={nameRef} data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-[#0A2E18]/25 rounded-md text-sm text-[#0A2E18] focus:outline-none focus:border-[#0A2E18] focus:ring-2 focus:ring-[#D98A00]/40" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">PHONE *</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-[#0A2E18]/25 rounded-md text-sm text-[#0A2E18] focus:outline-none focus:border-[#0A2E18] focus:ring-2 focus:ring-[#D98A00]/40" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">EMAIL</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-[#0A2E18]/25 rounded-md text-sm text-[#0A2E18] focus:outline-none focus:border-[#0A2E18] focus:ring-2 focus:ring-[#D98A00]/40" />
              </div>
              <div>
                <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">BUSINESS NAME</label>
                <input type="text" placeholder="Optional" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-[#0A2E18]/25 rounded-md text-sm text-[#0A2E18] focus:outline-none focus:border-[#0A2E18] focus:ring-2 focus:ring-[#D98A00]/40" />
              </div>
              <div>
                <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">JOB ADDRESS *</label>
                <input type="text" placeholder="Street, suburb, postcode" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-[#0A2E18]/25 rounded-md text-sm text-[#0A2E18] focus:outline-none focus:border-[#0A2E18] focus:ring-2 focus:ring-[#D98A00]/40" />
              </div>
              <div>
                <label className="block text-[11px] font-bold tracking-widest text-gray-600 mb-1">HOW CAN WE HELP? *</label>
                <textarea rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your lawn & garden needs..."
                  className="w-full px-3 py-2.5 border-2 border-[#0A2E18]/25 rounded-md text-sm text-[#0A2E18] focus:outline-none focus:border-[#0A2E18] focus:ring-2 focus:ring-[#D98A00]/40 resize-none" />
              </div>
              <PhotoUpload value={photos} onChange={setPhotos} variant="light" />
              <label className="flex items-start gap-2 text-sm text-[#212B20] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.agree}
                  onChange={(e) => setForm({ ...form, agree: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#1B6E37]"
                />
                <span>I agree to be contacted about my enquiry by phone, SMS or email.</span>
              </label>
              <button type="submit" disabled={submitting} className="btn-gold w-full py-3 rounded-md disabled:opacity-60">
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
